import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { ClipboardList, Search, Trash2, Plus, Pencil, Info } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';
import { UiSelect } from '../../components/UiSelect';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { SkeletonTable, EmptyState, ButtonLoader } from '../../components/LoadingStates';
import { recipeService } from '../../services/inventory/recipe';
import { inventoryItemService } from '../../services/inventory/inventoryItem';
import { itemService } from '../../services/item';
import { Recipe, RecipeIngredient, CreateRecipeRequest, unitShort } from '../../types/inventory';
import { Item } from '../../types';
import { formatQty } from './inventoryUtils';
import { Modal, Field, Th, inputCls } from './StockItemsTab';

interface Props { org: string; restaurantId: string; }

export const RecipesTab: React.FC<Props> = ({ org, restaurantId }) => {
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; recipeId: string; name: string }>({ open: false, recipeId: '', name: '' });

  const { data: menuItems, isLoading: itemsLoading } = useQuery(
    ['menu-items', org, restaurantId],
    () => itemService.getItems({ org, restaurantId }),
  );
  const { data: recipes, isLoading: recipesLoading } = useQuery(
    ['inventory-recipes', org, restaurantId],
    () => recipeService.getRecipes(org, restaurantId),
  );

  const recipeByItem = useMemo(() => {
    const map = new Map<string, Recipe>();
    recipes?.forEach(r => map.set(r.menuItemId, r));
    return map;
  }, [recipes]);

  const deleteMutation = useMutation(recipeService.deleteRecipe, {
    onSuccess: () => { queryClient.invalidateQueries(['inventory-recipes']); toast.success('Recipe removed'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to remove recipe'); },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return menuItems ?? [];
    return (menuItems ?? []).filter(i => i.name.toLowerCase().includes(q));
  }, [menuItems, search]);

  const isLoading = itemsLoading || recipesLoading;

  return (
    <div>
      <div className="flex items-start gap-2 mb-4 p-3 rounded-lg" style={{ backgroundColor: currentTheme.colors.primary[50] }}>
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: currentTheme.colors.primary[600] }} />
        <p className="text-sm" style={{ color: currentTheme.colors.primary[700] }}>
          A recipe lists the inventory ingredients a menu item consumes. When an order is placed, stock is automatically deducted based on the recipe.
        </p>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search menu items..."
          className="w-full pl-10 pr-4 py-2 rounded-xl border border-[var(--color-border-primary)] bg-[var(--color-background-secondary)] focus:outline-none focus:border-[var(--color-primary-400)] focus:ring-4 focus:ring-[var(--color-primary-100)]" />
      </div>

      {isLoading ? (
        <SkeletonTable rows={6} columns={4} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<ClipboardList className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No menu items"
          description="Add menu items first to define their recipes."
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
          <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
            <thead className="ui-thead">
              <tr><Th>Menu Item</Th><Th>Recipe</Th><Th>Ingredients</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="ui-tbody">
              {filtered.map(item => {
                const recipe = recipeByItem.get(item.id);
                return (
                  <tr key={item.id}>
                    <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>{item.name}</td>
                    <td className="px-4 py-3">
                      <span className={clsx('inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium', recipe ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600')}>
                        {recipe ? 'Defined' : 'Not set'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                      {recipe ? recipe.ingredients.map(ing => ing.itemName).filter(Boolean).join(', ') || `${recipe.ingredients.length} ingredient(s)` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setEditingItem(item)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                          style={{ color: currentTheme.colors.primary[700], backgroundColor: currentTheme.colors.primary[50] }}>
                          {recipe ? <><Pencil className="h-3.5 w-3.5" /> Edit</> : <><Plus className="h-3.5 w-3.5" /> Add</>}
                        </button>
                        {recipe && (
                          <button onClick={() => setConfirm({ open: true, recipeId: recipe.id, name: item.name })} className="p-2 rounded-lg" style={{ color: '#dc2626', backgroundColor: '#fef2f2' }}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingItem && (
        <RecipeForm
          org={org} restaurantId={restaurantId} menuItem={editingItem}
          existing={recipeByItem.get(editingItem.id) ?? null}
          onClose={() => setEditingItem(null)}
          onSaved={() => { setEditingItem(null); queryClient.invalidateQueries(['inventory-recipes']); }}
        />
      )}

      <ConfirmationModal
        isOpen={confirm.open}
        onClose={() => setConfirm(c => ({ ...c, open: false }))}
        onConfirm={() => { deleteMutation.mutate(confirm.recipeId); setConfirm(c => ({ ...c, open: false })); }}
        title="Remove Recipe"
        message={`Remove the recipe for "${confirm.name}"? Stock will no longer auto-deduct for this item.`}
        confirmText="Remove" type="danger" isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

interface DraftIng { inventoryItemId: string; quantity: string; }

const RecipeForm: React.FC<{ org: string; restaurantId: string; menuItem: Item; existing: Recipe | null; onClose: () => void; onSaved: () => void }> = ({ org, restaurantId, menuItem, existing, onClose, onSaved }) => {
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const [yieldQty, setYieldQty] = useState(String(existing?.yieldQuantity ?? 1));
  const [ingredients, setIngredients] = useState<DraftIng[]>(
    existing?.ingredients.length
      ? existing.ingredients.map(i => ({ inventoryItemId: i.inventoryItemId, quantity: String(i.quantity) }))
      : [{ inventoryItemId: '', quantity: '' }],
  );

  const { data: items } = useQuery(['inventory-items', org, restaurantId], () => inventoryItemService.getItems({ org, restaurantId }));

  const saveMutation = useMutation(
    (payload: CreateRecipeRequest) => existing ? recipeService.updateRecipe(existing.id, payload) : recipeService.createRecipe(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['inventory-recipes']);
        toast.success(existing ? 'Recipe updated' : 'Recipe saved');
        onSaved();
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to save recipe'); },
    },
  );

  const setIng = (i: number, patch: Partial<DraftIng>) => setIngredients(list => list.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addIng = () => setIngredients(list => [...list, { inventoryItemId: '', quantity: '' }]);
  const removeIng = (i: number) => setIngredients(list => list.length > 1 ? list.filter((_, idx) => idx !== i) : list);

  const validIngs = ingredients.filter(l => l.inventoryItemId && Number(l.quantity) > 0);
  const canSave = validIngs.length > 0 && Number(yieldQty) > 0;

  const handleSave = () => {
    if (!canSave) { toast.error('Add at least one ingredient with a quantity.'); return; }
    const payloadIngs: RecipeIngredient[] = validIngs.map(l => ({ inventoryItemId: l.inventoryItemId, quantity: Number(l.quantity) }));
    saveMutation.mutate({
      menuItemId: menuItem.id,
      yieldQuantity: Number(yieldQty) || 1,
      ingredients: payloadIngs,
      organizationCode: org,
      restaurantId,
    });
  };

  return (
    <Modal title={`Recipe — ${menuItem.name}`} onClose={onClose} wide>
      <div className="space-y-4">
        <Field label="Yield (servings produced by these quantities)">
          <input type="number" step="any" min="1" value={yieldQty} onChange={e => setYieldQty(e.target.value)} className={clsx(inputCls, 'sm:w-40')} />
        </Field>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>Ingredients</label>
            <button type="button" onClick={addIng} className="text-sm font-medium flex items-center gap-1" style={{ color: currentTheme.colors.primary[600] }}><Plus className="h-4 w-4" /> Add ingredient</button>
          </div>
          <div className="space-y-2">
            {ingredients.map((l, i) => {
              const item = items?.find(it => it.id === l.inventoryItemId);
              return (
                <div key={i} className="flex gap-2 items-center">
                  <UiSelect value={l.inventoryItemId} onChange={v => setIng(i, { inventoryItemId: v })} placeholder="Select ingredient" className="flex-1"
                    options={[{ value: '', label: 'Select ingredient' }, ...(items || []).filter(it => it.active).map(it => ({ value: it.id, label: it.name }))]} />
                  <div className="relative w-32">
                    <input type="number" step="any" min="0" value={l.quantity} onChange={e => setIng(i, { quantity: e.target.value })} className={inputCls} placeholder="Qty" />
                    {item && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">{unitShort(item.unit)}</span>}
                  </div>
                  <button type="button" onClick={() => removeIng(i)} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              );
            })}
          </div>
          {Number(yieldQty) > 1 && (
            <p className="mt-2 text-xs" style={{ color: currentTheme.colors.text.secondary }}>
              Quantities above produce {formatQty(Number(yieldQty))} servings; per-order deduction is divided accordingly.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          <button type="button" onClick={handleSave} disabled={!canSave || saveMutation.isLoading}
            className="px-4 py-2 btn-primary">
            {saveMutation.isLoading ? <ButtonLoader text="Saving..." /> : (existing ? 'Update Recipe' : 'Save Recipe')}
          </button>
        </div>
      </div>
    </Modal>
  );
};
